import { FC, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useRevealClip } from '../../../../hooks/use-reveal-clip.hook';

type SafeDropDownProps = {
  children: ReactNode;
  title: string;
};

/**
 * Per-SAFE row in the Conversion Details list. Keeps its own look, but reveals on the same 200ms
 * grid the rest of the SAFE and event forms use: the border and padding live on the innermost box
 * so they are measured as part of the animated height instead of appearing at full size on the
 * first frame, and the clip is held until the reveal finishes.
 */
export const SafeDropDown: FC<SafeDropDownProps> = ({ title, children }) => {
  const [isOpen, setOpen] = useState(false);
  const isClipped = useRevealClip(isOpen);

  return (
    <div className="flex w-full flex-col">
      <div
        className={twMerge(
          'flex items-center justify-between bg-gray-50 px-4 py-3 transition-[border-radius] duration-200 ease-in-out',
          isOpen ? 'rounded-t-md' : 'rounded-md',
        )}
      >
        <Tooltip childrenWrapperClassName="max-w-[320px]" content={title}>
          <span className="truncate text-sm font-[450] text-gray-700">{title}</span>
        </Tooltip>
        <div className="flex items-center gap-4">
          <div
            className="h-fit w-fit cursor-pointer p-1 transition-colors hover:bg-gray-50"
            onClick={() => {
              setOpen((prev) => !prev);
            }}
          >
            <ChevronDownIcon
              className={twMerge(
                'size-4 transition-transform duration-200 ease-in-out',
                isOpen && 'rotate-180',
              )}
              iconColor="#344054"
            />
          </div>
        </div>
      </div>
      <div
        className={twMerge(
          'grid grid-flow-row transition-all duration-200 ease-in-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className={twMerge(isClipped && 'overflow-hidden')}>
          <div className="rounded-b-xl border-[1px] border-gray-100 p-4">{children}</div>
        </div>
      </div>
    </div>
  );
};
